import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Users,
  RefreshCw,
  Search,
  MessageCircle,
  Sparkles,
  NotebookPen,
  Flame,
  Clock,
  TrendingUp,
  Target,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

/**
 * AdminCoachUsers (Phase 28)
 *
 * "Activity" admin tab. Shows app users and how they're engaging with the
 * Apex AI Coach — chats, pre-game plans, journal entries, streaks, last
 * active — and exposes a drilldown panel reusing the same progress signals
 * end users see on their own /app/progress dashboard.
 *
 * Zero LLM credit usage: every number is a Mongo aggregation over existing
 * collections (no summaries, embeddings, or AI calls in this view).
 */

const TIER_FILTERS = [
  { id: '',        label: 'All' },
  { id: 'pro',     label: 'Pro' },
  { id: 'trial',   label: 'Trial' },
  { id: 'premium', label: 'Premium' },
  { id: 'free',    label: 'Free' },
];

const TIER_BADGE_STYLES = {
  pro:     'bg-[#004C54]/10 text-[#004C54]',
  premium: 'bg-[#0B0C0D] text-white',
  free:    'bg-secondary text-foreground/70',
};

const fmtRelative = (iso) => {
  if (!iso) return 'Never';
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    if (diff < 0) return 'Just now';
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
};

const fmtDateOnly = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
};

const TrialPill = ({ until }) => {
  if (!until) return null;
  try {
    // Date.now() in the render body is intentional — the pill displays the
    // remaining trial countdown and re-renders when the parent refetches.
    const ms = new Date(until).getTime() - Date.now();
    if (ms <= 0) return null;
    const days = Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    return (
      <span
        className="ml-1.5 inline-flex items-center gap-1 px-1.5 h-4 rounded text-[9px] font-mono-accent font-bold uppercase tracking-[0.14em] bg-[#4FBCC7]/15 text-[#0a6066]"
        title={`Trial until ${fmtDateOnly(until)}`}
      >
        Trial · {days}d
      </span>
    );
    // try/catch in render body is intentional — a malformed date string
    // should silently downgrade to "no pill" rather than crash the row.
  } catch { return null; }
};

const TierBadge = ({ tier, trialUntil }) => {
  const style = TIER_BADGE_STYLES[tier] || TIER_BADGE_STYLES.free;
  const label = (tier || 'free').toUpperCase();
  return (
    <span className="inline-flex items-center">
      <span className={`inline-flex items-center px-2 h-5 rounded text-[10px] font-mono-accent font-bold uppercase tracking-[0.16em] ${style}`}>
        {label}
      </span>
      <TrialPill until={trialUntil} />
    </span>
  );
};

// Tiny inline bar chart for the 7-day activity strip.
// `bars` is the shape returned by the backend (_compute_weekly_activity_bars).
const WeeklyBars = ({ bars }) => {
  if (!Array.isArray(bars) || bars.length === 0) {
    return <div className="text-[12px] text-foreground/50">No activity yet.</div>;
  }
  const max = Math.max(1, ...bars.map((b) => Number(b.total || 0)));
  return (
    <div className="flex items-end gap-1.5 h-16" aria-hidden="true">
      {bars.map((b, i) => {
        const total = Number(b.total || 0);
        const pct = total / max;
        const h = Math.max(6, Math.round(pct * 64));
        const label = b.label || b.date || `D${i + 1}`;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`w-full rounded-t ${total > 0 ? 'bg-[#004C54]' : 'bg-secondary/70'}`}
              style={{ height: `${h}px` }}
              title={`${label}: ${total}`}
            />
            <div className="text-[9.5px] font-mono-accent uppercase tracking-[0.14em] text-foreground/55">
              {(label || '').slice(0, 3)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Sparkline-ish mood viz. Mood series points are { date, score }.
const MoodSpark = ({ series }) => {
  const data = Array.isArray(series) ? series.filter((p) => p && p.score != null) : [];
  if (data.length === 0) {
    return <div className="text-[12px] text-foreground/50">No journal mood data yet.</div>;
  }
  // Scores expected on a 1-5 (or 1-10) scale. Normalize over observed range.
  const scores = data.map((p) => Number(p.score));
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = Math.max(0.0001, max - min);
  return (
    <div className="flex items-end gap-[3px] h-10" aria-hidden="true">
      {data.map((p, i) => {
        const norm = (Number(p.score) - min) / range;
        const h = Math.round(10 + norm * 30);
        return (
          <div
            key={i}
            className="w-1.5 rounded-t bg-[#4FBCC7]/80"
            style={{ height: `${h}px` }}
            title={`${p.date || `#${i + 1}`}: ${p.score}`}
          />
        );
      })}
    </div>
  );
};

// Expandable Coach-session row used inside the Activity tab drilldown.
// Click the row to toggle the full transcript; the transcript is fetched on
// demand from /api/admin/coach-users/{user_id}/chats/{session_id} so the
// initial drilldown payload stays small even for power users.
const CoachSessionRow = ({ session, userId }) => {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && !transcript && userId) {
      setLoading(true);
      try {
        const data = await api.get(
          `/api/admin/coach-users/${encodeURIComponent(userId)}/chats/${encodeURIComponent(session.session_id)}`
        );
        setTranscript(data?.messages || []);
      } catch (err) {
        logger.warn('coach transcript load failed', err);
        toast.error(err?.message || 'Could not load transcript');
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  }, [open, transcript, userId, session.session_id]);

  return (
    <li
      className="rounded-lg border border-border/60 overflow-hidden"
      data-testid={`admin-coach-session-${session.session_id}`}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left p-3 hover:bg-secondary/30 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center justify-between text-[11px] text-foreground/55">
          <span>{session.messages} msg{session.messages === 1 ? '' : 's'}</span>
          <span className="inline-flex items-center gap-1.5">
            {fmtRelative(session.last_at)}
            {open
              ? <ChevronDown className="h-3 w-3" />
              : <ChevronRight className="h-3 w-3" />}
          </span>
        </div>
        {session.preview && (
          <div className="text-[12.5px] text-foreground/80 mt-1 line-clamp-2">
            “{session.preview}”
          </div>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border/40 bg-background/40">
          {loading ? (
            <div className="py-3 text-[12px] text-foreground/55 inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading transcript…
            </div>
          ) : !transcript || transcript.length === 0 ? (
            <div className="py-3 text-[12px] text-foreground/55 italic">No messages.</div>
          ) : (
            <ol
              className="space-y-2 mt-3"
              data-testid={`admin-coach-transcript-${session.session_id}`}
            >
              {transcript.map((m, i) => {
                const isUser = (m.role || '').toLowerCase() === 'user';
                return (
                  <li
                    key={i}
                    className={`text-[12.5px] leading-relaxed whitespace-pre-wrap px-3 py-2 rounded-lg ${
                      isUser
                        ? 'bg-[#004C54]/8 text-foreground'
                        : 'bg-secondary/50 text-foreground/85'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-[0.16em] font-mono-accent font-bold opacity-60">
                      <span>{isUser ? 'User' : 'Coach AI'}</span>
                      <span>{fmtRelative(m.created_at)}</span>
                    </div>
                    {m.content}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </li>
  );
};

export default function AdminCoachUsers() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, active_30d: 0, by_tier: { free: 0, pro: 0, premium: 0 } });
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [detail, setDetail] = useState(null);          // { user, weekly_activity, ... }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // Debounce the search input so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '200');
      if (debouncedSearch) qs.set('search', debouncedSearch);
      if (tier) qs.set('tier', tier);
      const data = await api.get(`/api/admin/coach-users?${qs.toString()}`);
      setRows(Array.isArray(data?.users) ? data.users : []);
      setStats(data?.stats || { total: 0, active_30d: 0, by_tier: { free: 0, pro: 0, premium: 0 } });
    } catch (err) {
      logger.error('AdminCoachUsers list failed', err);
      toast.error('Could not load app users');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, tier]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // openDetail handler — setState calls are the legitimate UI handoff
  // (open drawer → fetch → populate detail). Disable for this block.

  const openDetail = useCallback(async (userId) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await api.get(`/api/admin/coach-users/${encodeURIComponent(userId)}`);
      setDetail(data);
    } catch (err) {
      logger.error('AdminCoachUsers detail failed', err);
      toast.error('Could not load user detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const statCards = useMemo(() => ([
    { label: 'App users', value: stats.total, testid: 'admin-coach-stat-total' },
    { label: 'Active (30d)', value: stats.active_30d, testid: 'admin-coach-stat-active' },
    { label: 'Pro', value: stats.by_tier?.pro ?? 0, testid: 'admin-coach-stat-pro' },
    { label: 'Free', value: stats.by_tier?.free ?? 0, testid: 'admin-coach-stat-free' },
  ]), [stats]);

  return (
    <div className="space-y-6" data-testid="admin-coach-users">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold mb-1">
            Apex AI Coach
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Activity</h2>
          <p className="text-[13.5px] text-foreground/60 mt-1">
            App users and their engagement with the Coach. Click any row to see weekly activity, themes, mood, and recent sessions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchList} disabled={loading} data-testid="admin-coach-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        {statCards.map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold">
                {c.label}
              </div>
              <div className="text-2xl font-heading font-bold mt-1" data-testid={c.testid}>
                {c.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name…"
              className="pl-9"
              data-testid="admin-coach-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Tier filter">
            {TIER_FILTERS.map((t) => (
              <button
                key={t.id || 'all'}
                onClick={() => setTier(t.id)}
                role="tab"
                aria-selected={tier === t.id}
                data-testid={`admin-coach-tier-${t.id || 'all'}`}
                className={`px-2.5 h-8 rounded-md text-[12px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  tier === t.id
                    ? 'bg-[#0a0d0e] text-white'
                    : 'bg-secondary/60 text-foreground/70 hover:bg-secondary'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3" data-testid="admin-coach-loading">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-coach-empty">
              <Users className="h-8 w-8 mx-auto text-foreground/30 mb-3" />
              <p className="text-sm text-foreground/60">
                No app users match these filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-secondary/40 text-[10.5px] font-mono-accent uppercase tracking-[0.18em] text-foreground/55 font-semibold">
                  <tr>
                    <th className="text-left px-4 py-2.5">User</th>
                    <th className="text-left px-3 py-2.5">Tier</th>
                    <th className="text-right px-3 py-2.5"><MessageCircle className="inline h-3 w-3 mr-1" />Chats</th>
                    <th className="text-right px-3 py-2.5"><Sparkles className="inline h-3 w-3 mr-1" />Pre-game</th>
                    <th className="text-right px-3 py-2.5"><NotebookPen className="inline h-3 w-3 mr-1" />Journals</th>
                    <th className="text-left px-3 py-2.5"><Clock className="inline h-3 w-3 mr-1" />Last active</th>
                    <th className="text-left px-3 py-2.5">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {rows.map((u) => (
                    <tr
                      key={u.user_id}
                      onClick={() => openDetail(u.user_id)}
                      data-testid={`admin-coach-row-${u.user_id}`}
                      className="hover:bg-secondary/30 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-foreground/90 truncate max-w-[260px]">
                          {u.name || u.email}
                        </div>
                        {u.name && (
                          <div className="text-[11.5px] text-foreground/55 truncate max-w-[260px]">
                            {u.email}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <TierBadge tier={u.tier} trialUntil={u.trial_grant_until} />
                      </td>
                      <td className="text-right px-3 py-2.5 font-mono-accent font-semibold">
                        {u.chats || 0}
                      </td>
                      <td className="text-right px-3 py-2.5 font-mono-accent font-semibold">
                        {u.pregames || 0}
                      </td>
                      <td className="text-right px-3 py-2.5 font-mono-accent font-semibold">
                        {u.journals || 0}
                      </td>
                      <td className="px-3 py-2.5 text-foreground/70">
                        {fmtRelative(u.last_active)}
                      </td>
                      <td className="px-3 py-2.5 text-foreground/60 text-[11.5px]">
                        {u.signup_source === 'ai_coach_trial_popup' ? (
                          <Badge variant="outline" className="text-[10px]">Trial popup</Badge>
                        ) : (
                          <span className="font-mono-accent uppercase tracking-[0.14em] text-[10px]">
                            {u.signup_source || (u.audience || '—')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drilldown */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[520px] overflow-y-auto"
          data-testid="admin-coach-detail"
        >
          <SheetHeader>
            <SheetTitle className="font-heading">
              {detail?.user?.name || detail?.user?.email || 'User detail'}
            </SheetTitle>
            <SheetDescription className="text-[12.5px]">
              {detail?.user?.email}
            </SheetDescription>
          </SheetHeader>

          {detailLoading || !detail ? (
            <div className="mt-6 space-y-4">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : (
            <div className="mt-6 space-y-6" data-testid="admin-coach-detail-body">
              {/* Profile strip */}
              <div className="flex flex-wrap items-center gap-2">
                <TierBadge
                  tier={detail.user?.tier}
                  trialUntil={detail.user?.trial_grant_until}
                />
                {detail.user?.subscription_status && (
                  <Badge variant="outline" className="text-[10.5px]">
                    {detail.user.subscription_status}
                  </Badge>
                )}
                {detail.user?.is_admin && (
                  <Badge className="bg-[#0a0d0e] text-white text-[10.5px]">Admin</Badge>
                )}
                <div className="text-[11.5px] text-foreground/55 ml-auto">
                  Joined {fmtDateOnly(detail.user?.created_at)}
                </div>
              </div>

              {/* Headline stats */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { icon: MessageCircle, label: 'Chats',   value: detail.user?.chats ?? 0 },
                  { icon: Sparkles,      label: 'Pre-game', value: detail.user?.pregames ?? 0 },
                  { icon: NotebookPen,   label: 'Journals', value: detail.user?.journals ?? 0 },
                  { icon: Flame,         label: 'Streak',   value: `${detail.user?.streak_days ?? 0}d` },
                ].map((s) => (
                  <div key={s.label} className="rounded-lg border border-border/60 p-2.5">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-foreground/55 font-mono-accent font-semibold">
                      <s.icon className="h-3 w-3" />
                      {s.label}
                    </div>
                    <div className="text-lg font-heading font-bold mt-1">
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Weekly activity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold inline-flex items-center gap-1.5">
                    <TrendingUp className="h-3 w-3" />
                    Last 7 days
                  </div>
                  <div className="text-[11.5px] text-foreground/55">
                    Last active {fmtRelative(detail.user?.last_active)}
                  </div>
                </div>
                <WeeklyBars bars={detail.weekly_activity} />
              </div>

              {/* Mood trend */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold mb-2">
                  Mood trend (last 30 days)
                </div>
                <MoodSpark series={detail.mood_series} />
              </div>

              {/* Themes */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold mb-2">
                  Recurring themes
                </div>
                {Array.isArray(detail.themes) && detail.themes.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {detail.themes.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-[11px]">
                        #{t.label || t.keyword || t.term || t.word || t.value || '—'}
                        {t.count ? <span className="ml-1 text-foreground/45">· {t.count}</span> : null}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-[12px] text-foreground/50">No journal themes yet.</div>
                )}
              </div>

              {/* Recent sessions — click any row to expand the full transcript. */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold mb-2 inline-flex items-center gap-1.5">
                  <MessageCircle className="h-3 w-3" />
                  Recent Coach sessions
                </div>
                {detail.recent_sessions?.length ? (
                  <ul className="space-y-2">
                    {detail.recent_sessions.map((s) => (
                      <CoachSessionRow
                        key={s.session_id}
                        session={s}
                        userId={detail.user?.user_id}
                      />
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-foreground/50">No chat sessions yet.</div>
                )}
              </div>

              {/* Recent journals */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold mb-2 inline-flex items-center gap-1.5">
                  <NotebookPen className="h-3 w-3" />
                  Recent journal entries
                </div>
                {detail.recent_journals?.length ? (
                  <ul className="space-y-2">
                    {detail.recent_journals.map((j, i) => (
                      <li key={j.entry_id || j.id || i} className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-center justify-between text-[11px] text-foreground/55">
                          <span>{fmtRelative(j.created_at)}</span>
                          {j.mood && <Badge variant="outline" className="text-[10px]">{j.mood}</Badge>}
                        </div>
                        {(j.content || j.entry || j.reflection) && (
                          <div className="text-[12.5px] text-foreground/80 mt-1 line-clamp-3">
                            {j.content || j.entry || j.reflection}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-foreground/50">No journals yet.</div>
                )}
              </div>

              {/* Goals */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold mb-2 inline-flex items-center gap-1.5">
                  <Target className="h-3 w-3" />
                  Goals
                </div>
                {detail.goals?.length ? (
                  <ul className="space-y-1.5">
                    {detail.goals.map((g, i) => (
                      <li key={g.goal_id || i} className="text-[13px] flex items-start gap-2">
                        <span
                          className={`mt-1 inline-block h-2 w-2 rounded-full ${
                            g.completed_at ? 'bg-[#004C54]' : 'bg-foreground/30'
                          }`}
                          aria-hidden="true"
                        />
                        <div className="flex-1">
                          <div className={g.completed_at ? 'line-through text-foreground/55' : ''}>
                            {g.title || g.text || g.goal || '—'}
                          </div>
                          {g.created_at && (
                            <div className="text-[10.5px] text-foreground/45 font-mono-accent uppercase tracking-[0.14em]">
                              {fmtDateOnly(g.created_at)}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-foreground/50">No goals yet.</div>
                )}
              </div>

              <div className="pt-2 flex justify-end">
                <Button variant="outline" onClick={() => setDetailOpen(false)} data-testid="admin-coach-detail-close">
                  <X className="h-4 w-4 mr-1.5" />
                  Close
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
